import { Eyebrow, PrimaryLink } from "./components"

const MISSION_PARAGRAPH =
  "At Redempta Development Foundation, we believe that a child’s future should never be determined by the circumstances of their birth. Talent is universal, but opportunity is not. Our mission is to bridge that gap by providing quality education, transformative mentorship, and life-changing opportunities that empower children and young people to realize their fullest potential."

const STORY = [
  "Every child deserves the opportunity to dream—and the chance to pursue that dream.",
  MISSION_PARAGRAPH,
  "The vision for Redempta was inspired by the founders’ personal experiences of the transformative power of opportunity. Having experienced firsthand how access to quality education, exposure, and the right environment can shape a young person’s aspirations and trajectory, the founders came to understand that opportunity can fundamentally change what a child believes is possible for their life.",
  "For one of the founders, a scholarship during their formative years provided access to quality education and exposure that expanded their vision, nurtured their confidence, and gave them the audacity to dream beyond the limits of their circumstances. Although the scholarship ended before university due to a change in government, the impact of that opportunity endured and continued to shape the course of their life.",
  "These experiences revealed a powerful truth: when children are given access to quality education and the right environment at an early age, they develop the confidence, skills, and perspective to compete, thrive, and lead—regardless of where they come from.",
  "Redempta exists to create that opportunity for others.",
  "We invest in children and young people not only through education, but through mentorship, leadership development, life skills, and character formation. We believe true transformation extends beyond academic excellence. It shapes compassionate, confident, and purpose-driven individuals who become problem solvers, responsible leaders, and catalysts for positive change in their families, communities, and nations.",
  "Our work is guided by a simple conviction: possibilities are not limited by present circumstances. Give a child the audacity to dream, and you change the colors with which they paint the world.",
  "Inspired by the message of redemption, the name Redempta reflects our belief that every life carries immeasurable worth and extraordinary potential. While our faith inspires our vision, our programs are open to all, regardless of background, faith, or circumstance.",
  "We envision a world where every child has access to the opportunities, guidance, and encouragement needed to pursue their greatest potential. A world where dreams are nurtured, barriers are removed, and transformed lives become a lasting force for good.",
  "At Redempta, we do more than support education—we cultivate possibility. We walk alongside children and young people as they discover who they can become, equipping them to build meaningful lives and, in turn, create opportunities for others.",
  "Because when one life is transformed, generations are changed.",
]

const [MOBILE_INTRO_BEFORE_ORG, MOBILE_INTRO_AFTER_ORG] = MISSION_PARAGRAPH.split("Redempta Development Foundation")

const MOBILE_INTRO = (
  <>
    <p className="text-[15px] font-normal leading-[26px] text-ink/90">{STORY[0]}</p>
    <p className="text-[15px] font-normal leading-[26px] text-ink/90">
      {MOBILE_INTRO_BEFORE_ORG}
      <span className="text-forest-700">Redempta Development Foundation</span>
      {MOBILE_INTRO_AFTER_ORG}
    </p>
  </>
)

const MOBILE_STORY = STORY.slice(2)

const GOALS = [
  { number: "1", id: "SDG 1", name: "No Poverty", description: "Reducing the fee barrier that keeps children out of quality classrooms." },
  { number: "4", id: "SDG 4", name: "Quality Education", description: "Expanding access to quality private schools for undereducated and out-of-school children" },
  { number: "5", id: "SDG 5", name: "Gender Equality", description: "Micro-business education and grants for women." },
  { number: "10", id: "SDG 10", name: "Reduced Inequalities", description: "Bridging under-resourced public schools to quality private schools." },
  { number: "13", id: "SDG 13", name: "Climate Action", description: "Promoting climate-change education and awareness among children and young people — equipping them to take meaningful climate action." },
]

function FocusSection({
  label,
  title,
  body,
  image,
  imageAlt,
  linkTo,
  linkLabel,
  flip = false,
}: {
  label: string
  title: React.ReactNode
  body: string
  image: string
  imageAlt: string
  linkTo?: string
  linkLabel?: string
  flip?: boolean
}) {
  return (
    <div className={`about-focus-grid${flip ? " about-focus-grid--flip" : ""}`}>
      <div className="about-focus-content">
        <div className="about-focus-heading">
          <Eyebrow dot>{label}</Eyebrow>
          <h2 className="font-display text-[32px] font-bold leading-[48px] text-ink sm:text-[40px] lg:text-[48px]">
            {title}
          </h2>
        </div>
        <div className="about-focus-body">
          <p className="text-[15px] leading-[22px] text-ink/80 lg:text-lg lg:leading-[25px]">{body}</p>
          {linkTo && linkLabel && (
            <PrimaryLink to={linkTo} className="mt-7 max-w-fit">
              {linkLabel}
            </PrimaryLink>
          )}
        </div>
      </div>
      <img
        src={image}
        alt={imageAlt}
        className="about-focus-image w-full rounded-2xl object-cover lg:rounded-[30px]"
      />
    </div>
  )
}

export function AboutPage() {
  return (
    <>
      <section className="bg-cream-50 pb-10 pt-10 lg:mt-[60px] lg:pb-0 lg:pt-0">
        <div className="public-container">
          <div className="about-hero relative h-[170px] overflow-hidden rounded-[30px] bg-black lg:h-[525px]">
            <img
              src="/images/pages/about-hero.jpg"
              alt="Redempta scholars in a classroom"
              className="hidden h-full w-full object-cover lg:block"
            />
            <div className="absolute inset-0 hidden bg-black/50 lg:block" />
            <h1 className="absolute inset-0 grid place-items-center text-center font-display text-[40px] font-bold leading-[48px] text-white lg:inset-auto lg:bottom-10 lg:left-10 lg:block lg:max-w-[1118px] lg:text-left lg:text-[64px] lg:leading-[64px]">
              <span className="lg:hidden">
                About <span className="text-forest-700">Us</span>
              </span>
              <span className="hidden lg:block">About Us</span>
            </h1>
          </div>
        </div>
      </section>

      <section className="about-story bg-cream-50 lg:min-h-[914px]">
        <div className="public-container">
          <div className="hidden lg:block lg:pt-[30px]">
            <div className="text-center">
              <Eyebrow dot>About the Foundation</Eyebrow>
            </div>
            <div className="mt-[30px] h-px w-full bg-ink/10" />
            <div className="mt-[30px] space-y-4 text-center text-lg font-medium leading-[35px] text-ink/90">
              {STORY.map((paragraph, index) => (
                <p
                  key={paragraph.slice(0, 32)}
                  className={index === 0 || index === 5 || index === STORY.length - 1 ? "text-forest-700" : undefined}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
          <div className="lg:hidden">
            <div className="about-story__intro flex flex-col gap-4">
              <Eyebrow dot>About the Foundation</Eyebrow>
              {MOBILE_INTRO}
            </div>
            <div className="mt-10 space-y-4 text-[15px] font-normal leading-[26px] text-ink/90">
              {MOBILE_STORY.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="about-focus-section bg-cream-50 lg:min-h-[685px] lg:py-[93px]">
        <div className="public-container">
          <FocusSection
            label="The Primary Focus"
            title={<>Children first.<br /><span className="text-forest-700">Always.</span></>}
            body="Roughly 80% of everything we do — funding, energy, attention — goes directly to keeping children in school. That is the promise, and we audit ourselves against it every term."
            image="/images/pages/about-hero.jpg"
            imageAlt="Children learning in a partner school classroom"
          />
        </div>
      </section>

      <section className="about-focus-section py-[20px] lg:py-[0px] bg-cream-100 lg:min-h-[685px] lg:py-[93px]">
        <div className="public-container">
          <FocusSection
            label="Expanding the Circle"
            title={<>Ability Program.<br /><span className="text-forest-700">Opportunity for all</span></>}
            body="We also support people living with physical disabilities across every age: children, teens, and university students. Mobility aids, hearing devices, visual support, therapy, school fees, and adapted skills training — so ability is never limited by access."
            image="/images/pages/about-ability.jpg"
            imageAlt="A beneficiary of the Ability Program"
            linkTo="/ability-program"
            linkLabel="Explore the Ability Program"
            flip
          />
        </div>
      </section>

      <section className="about-focus-section bg-cream-50 lg:min-h-[685px] lg:py-[93px]">
        <div className="public-container">
          <FocusSection
            label="Alongside the Children"
            title={<>Empowering the women<br /><span className="text-forest-700">around them.</span></>}
            body="Mothers, aunts, guardians — the women in a child's life often hold the household together on a small trade. We teach business principles and, where we can, extend micro-grants — because a child stays and thrives in school more easily when the home stays steady."
            image="/images/pages/about-women.jpg"
            imageAlt="A woman running her small business"
            linkTo="/womens-empowerment"
            linkLabel="Explore the Women’s Program"
          />
        </div>
      </section>

      <section className="about-global-section bg-cream-50 px-0 pb-10 pt-10 text-center lg:min-h-[604px] lg:py-[60px]">
        <div className="public-container">
          <Eyebrow dot>Global Alignment</Eyebrow>
          <div className="mx-auto mt-4 max-w-5xl">
            <h2 className="font-display text-[30px] font-bold leading-[45px] text-forest-700 sm:text-[40px] lg:text-[48px] lg:leading-tight">
              Sustainable Development Goals
            </h2>
            <p className="mt-4 text-lg text-ink/80">
              Redempta&apos;s work maps to five UN SDGs. As we scale, this alignment opens doors to global partners, grants, and shared measurement.
            </p>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 lg:mt-12 lg:grid-cols-4 lg:gap-6">
            {GOALS.map((goal) => (
              <article key={goal.number} className="min-h-[209px] rounded-xl bg-white p-4 text-left lg:min-h-0 lg:p-6">
                <span className="grid h-[38px] w-[38px] place-items-center rounded-lg bg-forest-950 font-display text-xl font-bold leading-none text-white lg:h-[49px] lg:w-[49px] lg:text-[28px]">
                  {goal.number}
                </span>
                <p className="mt-3 text-xs text-ink/60 lg:mt-8 lg:text-base">{goal.id}</p>
                <h3 className="mt-1 text-sm font-semibold text-ink lg:text-base">{goal.name}</h3>
                <p className="mt-2 text-xs leading-relaxed text-ink/70 lg:mt-3 lg:text-[15px]">{goal.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
